import React from 'react';
import { Card, Button, Typography, Row, Col, Space, Tag, Collapse } from 'antd';
import {
  DollarOutlined, RocketOutlined, LinkOutlined, TeamOutlined, MailOutlined,
  WhatsAppOutlined, WalletOutlined, CheckCircleOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// Onboarding autenticado do modulo Financeiro. Renderizado quando o time
// atual (active_team_id) ainda nao tem community_accounts criado.
//
// Props:
//   teamName: string
//   onActivate: () => Promise<void>  — chamado ao clicar "Ativar"
//   activating: boolean               — desabilita botao enquanto POST /activate
//   notInBeta: boolean                — mostra msg de beta em vez do CTA normal
export default function FinanceOnboarding({ teamName, onActivate, activating, notInBeta }) {
  return (
    <div style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
      {/* HERO */}
      <Card style={{ background: 'linear-gradient(135deg, #0b1f1a 0%, #128C7E 100%)', color: '#fff', border: 'none', marginBottom: 16 }}>
        <div style={{ padding: '20px 8px', textAlign: 'center' }}>
          <Title level={2} style={{ color: '#fff', margin: 0 }}>
            Gestão financeira do seu time. De graça.
          </Title>
          <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16, marginTop: 12 }}>
            Ativa agora e usa saldo, extrato e controle sem pagar nada.
            <br />
            <strong style={{ color: '#fff' }}>Só paga se decidir enviar cobrança pros jogadores</strong> — aí compra créditos e conecta sua conta Mercado Pago pra receber.
          </Paragraph>
          {notInBeta ? (
            <Tag color="orange" style={{ padding: '8px 16px', fontSize: 14, marginTop: 12 }}>
              Estamos rolando o beta pra alguns times primeiro. Sem fila, sem cadastro · a gente te chama.
            </Tag>
          ) : (
            <Button
              type="primary" size="large"
              loading={activating}
              onClick={onActivate}
              icon={<RocketOutlined />}
              style={{ background: '#25D366', borderColor: '#25D366', marginTop: 12, height: 48, fontSize: 16, fontWeight: 700 }}>
              Ativar Financeiro pro {teamName}
            </Button>
          )}
        </div>
      </Card>

      {/* TRANSPARÊNCIA DE PREÇO */}
      <Card title={<><DollarOutlined /> Como funciona a cobrança</>} style={{ marginBottom: 16 }}>
        <Paragraph style={{ margin: 0, marginBottom: 12 }}>
          A parte de <strong>gestão</strong> (saldo, extrato, contatos, lançar entrada/saída) é <strong>grátis pra sempre</strong>. Você só paga se decidir enviar cobranças automáticas pros jogadores — nesse caso precisa de créditos + conta MP conectada.
        </Paragraph>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <div style={{ padding: 12, background: '#f0fdf4', borderRadius: 8, border: '1px solid #b7eb8f' }}>
              <Text strong style={{ fontSize: 20, color: '#128C7E' }}>R$ 0</Text>
              <br />
              <Text>Usar a gestão financeira. Sem mensalidade, sem plano.</Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
              <Text strong style={{ fontSize: 20 }}>R$ 1</Text>
              <br />
              <Text>Por cobrança enviada (email incluso, WhatsApp em breve).</Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
              <Text strong style={{ fontSize: 20 }}>+ R$ 1</Text>
              <br />
              <Text>Pra reforçar via WhatsApp quem não pagou.</Text>
            </div>
          </Col>
        </Row>
        <Paragraph style={{ marginTop: 16, background: '#fffbe6', padding: 12, borderRadius: 8, border: '1px solid #ffe58f' }}>
          <Text strong>Exemplo real:</Text> cobrança de R$ 100 paga via Pix · jogador manda R$ 100 e cai
          <Text strong> R$ 99,01</Text> na sua conta Mercado Pago (só a taxa MP de ~1% pra Pix, ~5% pra cartão).
          Nossa taxa é apenas 1 crédito (R$ 1) por cobrança gerada, debitado do seu saldo.
        </Paragraph>
      </Card>

      {/* COMO FUNCIONA */}
      <Card title={<><RocketOutlined /> Como funciona</>} style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Step n="1" icon={<RocketOutlined />} title="Ativa o financeiro (grátis)"
            desc="Só clica em Ativar. Já pode usar saldo, extrato e cadastrar contatos." />
          <Step n="2" icon={<LinkOutlined />} title="Só quando for cobrar: conecta seu Mercado Pago"
            desc="É grátis, uma vez só. Sem MP conectado, o dinheiro dos jogadores não tem pra onde ir." />
          <Step n="3" icon={<WalletOutlined />} title="Compra créditos (R$ 1 por cobrança)"
            desc="Você compra pacotes com Pix ou cartão. Sem crédito, sem cobrança — sem risco de gastar sem querer." />
          <Step n="4" icon={<TeamOutlined />} title="Escolhe quem cobrar"
            desc="Contatos, grupos, roster do time, Excel, jogadores do Futezap Pro, presentes numa pelada. O que preferir." />
          <Step n="5" icon={<MailOutlined />} title="Envia com Pix e cartão"
            desc="Cada jogador recebe o próprio link. Você acompanha pagos e devedores em tempo real." />
          <Step n="6" icon={<CheckCircleOutlined />} title="Dinheiro cai direto na sua conta MP"
            desc="Pix na hora, cartão em ~14 dias (regra do MP). A gente é só o mensageiro." />
        </Space>
      </Card>

      {/* FAQ */}
      <Card title="Perguntas frequentes" style={{ marginBottom: 16 }}>
        <Collapse ghost>
          <Panel header="Vocês guardam meu dinheiro?" key="1">
            <Text>Não. O dinheiro do jogador vai direto pra sua conta Mercado Pago. A gente cobra R$ 2 no ato (split MP).</Text>
          </Panel>
          <Panel header="E se eu quiser cancelar?" key="2">
            <Text>Nada pra cancelar. Não tem mensalidade nem plano. Se parar de cobrar, para de pagar.</Text>
          </Panel>
          <Panel header="Preciso ser Futezap Pro pra usar?" key="3">
            <Text>Não. Funciona pra qualquer time comunidade.</Text>
          </Panel>
          <Panel header="Quando o dinheiro entra na minha conta?" key="4">
            <Text>Pix na hora. Cartão em 14 dias (regra do Mercado Pago pra marketplaces).</Text>
          </Panel>
        </Collapse>
      </Card>

      {/* CTA final */}
      {!notInBeta && (
        <Card style={{ textAlign: 'center' }}>
          <Title level={4}>Bora ativar?</Title>
          <Text type="secondary">Sem cartão, sem burocracia. Ativação é grátis e leva 30 segundos.</Text>
          <br />
          <Button
            type="primary" size="large"
            loading={activating}
            onClick={onActivate}
            icon={<RocketOutlined />}
            style={{ background: '#25D366', borderColor: '#25D366', marginTop: 16, height: 48, fontSize: 16, fontWeight: 700 }}>
            Ativar Financeiro pro {teamName}
          </Button>
        </Card>
      )}
    </div>
  );
}

function Step({ n, icon, title, desc }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{
        minWidth: 40, height: 40, borderRadius: 20, background: '#25D366', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900,
      }}>{n}</div>
      <div style={{ flex: 1 }}>
        <Text strong style={{ fontSize: 15 }}>{icon} {title}</Text>
        <br />
        <Text type="secondary">{desc}</Text>
      </div>
    </div>
  );
}
